// UmpireGameCard — single upcoming weekend game rendered in the umpire
// workspace and the dashboard.
//
// Responsibilities:
//   • Show teams, date, ground, time in a clear card layout.
//   • Show a live "Locks in Xh Ym" countdown to the Thursday 9pm ET
//     deadline — flips to "Locked" once past.
//   • Show volunteer list (Assigned rows highlighted; Nominated below).
//   • Volunteer / Withdraw action button that respects the lock.
//
// Props:
//   poll           — one entry from api.listMyPolls() (with volunteers[], lockAt, isLocked)
//   busy           — parent controls the loading state on the action button
//   onNominate     — () => Promise, called when user taps "I'm available"
//   onWithdraw     — () => Promise, called when user taps "Withdraw"
//
// Deliberately dumb — no fetching, no toast. Parent handles both.

import React, { useEffect, useState } from "react";
import {
  MapPin, Clock, CheckCircle2, Ban, Lock, Users, Loader2, Crown, Calendar
} from "lucide-react";
import { fmtMatchDate, fmt12 } from "../views/league/shared";

// ── Countdown helper ────────────────────────────────────────────
function fmtCountdown(msRemaining) {
  if (msRemaining <= 0) return "Locked";
  const totalMins = Math.floor(msRemaining / 60_000);
  const days  = Math.floor(totalMins / (60 * 24));
  const hours = Math.floor((totalMins % (60 * 24)) / 60);
  const mins  = totalMins % 60;
  if (days   > 0) return `Locks in ${days}d ${hours}h`;
  if (hours  > 0) return `Locks in ${hours}h ${mins}m`;
  if (mins   > 5) return `Locks in ${mins}m`;
  return "Locks in a few minutes";
}

// ── LockCountdown — self-refreshing text ───────────────────────
function LockCountdown({ lockAt, isLocked }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Tick every 60s — cheap, and human-perceived lag on the countdown
    // is fine. Interval clears cleanly on unmount.
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  if (!lockAt) return null;
  const target = new Date(lockAt).getTime();
  const remaining = target - now;
  const derivedLocked = isLocked || remaining <= 0;
  return (
    <span className={`umpCard-lock${derivedLocked ? " umpCard-lock--locked" : ""}`}>
      {derivedLocked
        ? <><Lock size={11}/> Selections locked</>
        : <><Clock size={11}/> {fmtCountdown(remaining)}</>}
    </span>
  );
}

// ── Volunteer chip ──────────────────────────────────────────────
function VolunteerChip({ v }) {
  const initials = `${(v.firstName || "?")[0] || ""}${(v.lastName || "")[0] || ""}`.toUpperCase();
  const assigned = v.status === "Assigned";
  return (
    <span
      className={`umpCard-vol${assigned ? " umpCard-vol--assigned" : ""}`}
      title={`${v.firstName || ""} ${v.lastName || ""}${v.level ? ` · ${v.level}` : ""}`}
    >
      <span className="umpCard-vol-avatar">{initials}</span>
      <span className="umpCard-vol-name">
        {v.firstName} {v.lastName}
        {assigned && <Crown size={10} className="umpCard-vol-crown"/>}
      </span>
    </span>
  );
}

// ── Main card ──────────────────────────────────────────────────
export default function UmpireGameCard({ poll, busy, onNominate, onWithdraw }) {
  const mine       = poll.myStatus;
  const isAssigned = mine === "Assigned";
  const isNominated= mine === "Nominated";
  const nowLocked  = poll.isLocked;

  const assigned  = (poll.volunteers || []).filter(v => v.status === "Assigned");
  const nominated = (poll.volunteers || []).filter(v => v.status === "Nominated");

  // Card variant drives the accent stripe color + status pill.
  const variant = isAssigned  ? "assigned"
                : nowLocked   ? "locked"
                : isNominated ? "nominated"
                : "open";

  return (
    <article className={`umpCard umpCard--${variant}`}>
      <header className="umpCard-head">
        <div className="umpCard-date">
          <Calendar size={12}/>
          <b>{fmtMatchDate(poll.date)}</b>
          {poll.time && <><span className="umpCard-dot">·</span><Clock size={11}/> {fmt12(poll.time)} ET</>}
        </div>
        <LockCountdown lockAt={poll.lockAt} isLocked={poll.isLocked} />
      </header>

      <div className="umpCard-teams">
        <span className="umpCard-team">{poll.teamOne}</span>
        <span className="umpCard-vs">vs</span>
        <span className="umpCard-team">{poll.teamTwo}</span>
      </div>

      <div className="umpCard-ground">
        <MapPin size={12}/> {poll.ground}
        {poll.series && <span className="umpCard-series">· {poll.series}</span>}
      </div>

      {/* Volunteers block — Assigned surface first, Nominated below.
          If nobody has volunteered yet, show a friendly empty line. */}
      <div className="umpCard-volList">
        {assigned.length > 0 && (
          <div className="umpCard-volGroup umpCard-volGroup--assigned">
            <span className="umpCard-volGroup-lbl"><Crown size={11}/> Assigned</span>
            <div className="umpCard-volRow">
              {assigned.map(v => <VolunteerChip key={v.umpireId} v={v}/>)}
            </div>
          </div>
        )}
        <div className="umpCard-volGroup">
          <span className="umpCard-volGroup-lbl">
            <Users size={11}/> Volunteers <em>{nominated.length}</em>
          </span>
          <div className="umpCard-volRow">
            {nominated.length === 0 ? (
              <span className="umpCard-volEmpty">
                {nowLocked ? "No volunteers" : "Be the first to volunteer"}
              </span>
            ) : (
              nominated.map(v => <VolunteerChip key={v.umpireId} v={v}/>)
            )}
          </div>
        </div>
      </div>

      <footer className="umpCard-foot">
        {isAssigned ? (
          <span className="umpCard-statusBig umpCard-statusBig--assigned">
            <CheckCircle2 size={14}/> You're officiating this match
          </span>
        ) : isNominated ? (
          <button
            type="button"
            className="umpCard-btn umpCard-btn--withdraw"
            onClick={onWithdraw}
            disabled={busy || nowLocked}
            title={nowLocked ? "Selections are locked" : "Withdraw your volunteer"}
          >
            {busy
              ? <><Loader2 size={13} className="umpCard-spin"/> Working…</>
              : <><Ban size={13}/> Withdraw</>}
          </button>
        ) : (
          <button
            type="button"
            className="umpCard-btn umpCard-btn--in"
            onClick={onNominate}
            disabled={busy || nowLocked || !poll.pollOpen}
            title={
              nowLocked        ? "Selections are locked"
              : !poll.pollOpen ? "Poll not open yet"
              : "Volunteer for this game"
            }
          >
            {busy
              ? <><Loader2 size={13} className="umpCard-spin"/> Submitting…</>
              : <><CheckCircle2 size={13}/> I'm available</>}
          </button>
        )}
      </footer>
    </article>
  );
}
